import React, { useState } from 'react'

import {
    Modal,
    Paper,
    Typography,
    IconButton,
    Button,
    Container,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Popover,
    FormControl,
    FormControlLabel,
    Checkbox,
    Box,
    createStyles,
    makeStyles,
    Theme,
} from '@material-ui/core'
import { LeaseAuditController } from '../../../hooks/documentAudit/useLeaseAudit'
import { DocumentBatch, EntityValue } from '../../../models/DocumentAudit'
import { CircularProgress } from '@material-ui/core'
import { DropFiles } from '../../../components/DropFiles'
import DeleteIcon from '@material-ui/icons/Delete'
import RefreshIcon from '@material-ui/icons/Refresh'
import { Slide } from '@material-ui/core'

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '80vw',
            maxWidth: 800,
            maxHeight: '80vh',
            overflow: 'auto',
        },
        header: {
            padding: theme.spacing(2),
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
        tabContent: {
            minHeight: 300,
        },
        fileInput: {
            display: 'none',
        },
        uploadButton: {
            marginTop: theme.spacing(2),
        },
        formControl: {
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(2),
            width: '100%',
        },
        fileList: {
            marginTop: theme.spacing(3),
            marginBottom: theme.spacing(2),
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
            maxHeight: 200,
            overflow: 'auto',
        },
        fileListItem: {
            borderBottom: `1px solid ${theme.palette.divider}`,
            '&:last-child': {
                borderBottom: 'none',
            },
        },
        deleteButton: {
            color: theme.palette.error.main,
        },
        divider: {
            margin: theme.spacing(2, 0),
        },
    }),
)

interface RentRollDocumentManagerProps {
    open: boolean
    onClose: () => void
    leaseAuditController: LeaseAuditController
}

export const RentRollDocumentManagerModal = (
    props: RentRollDocumentManagerProps,
) => {
    const { open, onClose, leaseAuditController } = props
    const classes = useStyles()
    const [uploading, setUploading] = useState(false)

    const [
        deletePopoverAnchorEl,
        setDeletePopoverAnchorEl,
    ] = useState<HTMLElement | null>(null)
    const [batchToDelete, setBatchToDelete] = useState<number | null>(null)

    // Add state for Entrata sync confirmation
    const [
        syncPopoverAnchorEl,
        setSyncPopoverAnchorEl,
    ] = useState<HTMLElement | null>(null)

    // Add state for Entrata terms selection
    const [entrataTerms, setEntrataTerms] = useState<{
        current: boolean
        future: boolean
        past: boolean
    }>({
        current: false,
        future: true,
        past: false,
    })

    const documentBatches: DocumentBatch[] =
        leaseAuditController.documentBatches

    // filter document batches to only include rent roll
    const filteredDocumentBatches = documentBatches.filter((batch) => {
        // Check if the batch has document_occurances with EXCEL document_type
        return batch.document_occurances.some(
            (occurrence) => occurrence.document.document_type === 'EXCEL',
        )
    })

    // Format UTC date string to local time
    const formatDateTimeLocal = (utcDateString: string): string => {
        try {
            const date = new Date(utcDateString)

            // Check if date is valid
            if (isNaN(date.getTime())) {
                return utcDateString
            }

            // Format date part - MM/DD/YYYY format (more standard in US)
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            const dateStr = `${month}/${day}/${year}`

            // Format time part - h:mm AM/PM (omitting seconds for cleaner display)
            let hours = date.getHours()
            const minutes = String(date.getMinutes()).padStart(2, '0')
            const ampm = hours >= 12 ? 'PM' : 'AM'
            hours = hours % 12
            hours = hours ? hours : 12 // Convert 0 to 12
            const timeStr = `${hours}:${minutes} ${ampm}`

            // Combine with clear visual separator
            return `${dateStr} at ${timeStr}`
        } catch (error) {
            console.error('Error formatting date:', error)
            return utcDateString
        }
    }

    // Update to show confirmation popover first
    const handleEntrataSyncClick = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        setSyncPopoverAnchorEl(event.currentTarget)
    }

    // Cancel sync
    const handleSyncCancel = () => {
        setSyncPopoverAnchorEl(null)
    }

    // Confirm and execute sync
    const handleEntrataSync = () => {
        setUploading(true)
        setSyncPopoverAnchorEl(null)

        if (leaseAuditController.leaseAudit) {
            const selectedTerms = Object.entries(entrataTerms)
                .filter(([_, selected]) => selected)
                .map(([term]) => term)
                .join(',')

            if (selectedTerms.length === 0) {
                // No terms selected, show error
                setUploading(false)
                return
            }

            leaseAuditController.syncEntrataRentRoll(
                leaseAuditController.leaseAudit.id,
                selectedTerms,
            )
        }
    }

    const handleDeleteBatchClick = (
        event: React.MouseEvent<HTMLButtonElement>,
        batchId: number,
    ) => {
        setDeletePopoverAnchorEl(event.currentTarget)
        setBatchToDelete(batchId)
    }

    const handleDeleteCancel = () => {
        setDeletePopoverAnchorEl(null)
        setBatchToDelete(null)
    }

    const handleDeleteBatchConfirm = () => {
        if (batchToDelete && leaseAuditController.leaseAudit) {
            leaseAuditController.deleteDocumentBatch(batchToDelete)
            leaseAuditController.getRentRoll(leaseAuditController.leaseAudit.id)
        }
        handleDeleteCancel()
    }

    // Helper function to get file name from batch
    const getBatchFileName = (batch: DocumentBatch): string => {
        if (
            batch.document_occurances &&
            batch.document_occurances.length > 0 &&
            batch.document_occurances[0].document
        ) {
            return batch.document_occurances[0].document.file_name
        }
        return 'Unknown file'
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Paper className={classes.root}>
                <div className={classes.header}>
                    <Container
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography variant="h6">
                            Rent Roll Document Manager
                        </Typography>
                        <IconButton
                            aria-label="refresh"
                            onClick={() => {
                                if (leaseAuditController.leaseAudit) {
                                    leaseAuditController.getRentRoll(
                                        leaseAuditController.leaseAudit.id,
                                    )
                                    leaseAuditController.fetchDocumentBatches(
                                        leaseAuditController.leaseAudit.id,
                                    )
                                    setUploading(false)
                                }
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Container>
                </div>

                <Box p={3}>
                    {/* Import options in a single row */}
                    <Container
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 16,
                        }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleEntrataSyncClick}
                            disabled={uploading}
                        >
                            {uploading ? (
                                <CircularProgress size={24} />
                            ) : (
                                'Sync with Entrata'
                            )}
                        </Button>

                        <Container
                            style={{
                                flex: 1,
                                maxWidth: '60%',
                                marginLeft: 16,
                            }}
                        >
                            <DropFiles
                                onDrop={(files) => {
                                    if (leaseAuditController.leaseAudit) {
                                        leaseAuditController.uploadLeaseContractBatch(
                                            files,
                                            leaseAuditController.leaseAudit.id,
                                        )
                                    }
                                }}
                                displayString="Drop or Click to upload excel rent roll file."
                            />
                        </Container>
                    </Container>

                    <Divider className={classes.divider} />

                    {/* Sync Logs List */}
                    <Typography
                        variant="subtitle1"
                        gutterBottom
                        style={{ marginTop: 16 }}
                    >
                        Rent Roll Import History
                    </Typography>
                    <List className={classes.fileList}>
                        {filteredDocumentBatches.length === 0 ? (
                            <ListItem className={classes.fileListItem}>
                                <ListItemText primary="No import history available" />
                            </ListItem>
                        ) : (
                            filteredDocumentBatches.map((batch) => (
                                <ListItem
                                    key={batch.id}
                                    className={classes.fileListItem}
                                >
                                    <ListItemText
                                        primary={getBatchFileName(batch)}
                                        secondary={
                                            <>
                                                {formatDateTimeLocal(
                                                    batch.created_date,
                                                )}{' '}
                                                •{' '}
                                                {
                                                    batch.document_occurances
                                                        .length
                                                }{' '}
                                                records
                                            </>
                                        }
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton
                                            edge="end"
                                            aria-label="delete"
                                            className={classes.deleteButton}
                                            onClick={(e) =>
                                                handleDeleteBatchClick(
                                                    e,
                                                    batch.id,
                                                )
                                            }
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))
                        )}
                    </List>
                </Box>

                {/* Delete Confirmation Popover */}
                <Popover
                    open={Boolean(deletePopoverAnchorEl)}
                    anchorEl={deletePopoverAnchorEl}
                    onClose={handleDeleteCancel}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                >
                    <Paper style={{ padding: 16, maxWidth: 300 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Confirm Deletion
                        </Typography>
                        <Typography variant="body2" paragraph>
                            Are you sure you want to delete this import record
                            and its data?
                        </Typography>
                        <Container
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Button
                                onClick={handleDeleteCancel}
                                color="primary"
                                size="small"
                                style={{ marginRight: 8 }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleDeleteBatchConfirm}
                                color="secondary"
                                variant="contained"
                                size="small"
                            >
                                Delete
                            </Button>
                        </Container>
                    </Paper>
                </Popover>

                {/* Entrata Sync Confirmation Popover */}
                <Popover
                    open={Boolean(syncPopoverAnchorEl)}
                    anchorEl={syncPopoverAnchorEl}
                    onClose={handleSyncCancel}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                >
                    <Paper style={{ padding: 16, maxWidth: 300 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Confirm Entrata Sync
                        </Typography>
                        <Typography variant="body2" paragraph>
                            Select which lease terms to import:
                        </Typography>

                        <FormControl
                            component="fieldset"
                            style={{ marginBottom: 16 }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={entrataTerms.current}
                                        onChange={(e) =>
                                            setEntrataTerms({
                                                ...entrataTerms,
                                                current: e.target.checked,
                                            })
                                        }
                                        color="primary"
                                    />
                                }
                                label="Current"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={entrataTerms.future}
                                        onChange={(e) =>
                                            setEntrataTerms({
                                                ...entrataTerms,
                                                future: e.target.checked,
                                            })
                                        }
                                        color="primary"
                                    />
                                }
                                label="Future"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={entrataTerms.past}
                                        onChange={(e) =>
                                            setEntrataTerms({
                                                ...entrataTerms,
                                                past: e.target.checked,
                                            })
                                        }
                                        color="primary"
                                    />
                                }
                                label="Past"
                            />
                        </FormControl>

                        <Container
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Button
                                onClick={handleSyncCancel}
                                color="primary"
                                size="small"
                                style={{ marginRight: 8 }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleEntrataSync}
                                color="primary"
                                variant="contained"
                                size="small"
                                disabled={
                                    !Object.values(entrataTerms).some(Boolean)
                                }
                            >
                                Sync
                            </Button>
                        </Container>
                    </Paper>
                </Popover>

                <Box p={2} display="flex" justifyContent="flex-end">
                    <Button onClick={onClose} color="secondary">
                        Close
                    </Button>
                </Box>
            </Paper>
        </Modal>
    )
}
